import { defineStore } from 'pinia';
import { applyCorrections, mergeMutualSwaps } from '@vft/core/transform/pipeline.js';
import { buildOutputRows, validateOutputRows } from '@vft/core/export/exporter.js';
import { fetchCorrectionPrograms } from '@vft/api/programService.js';
import { getCorrectionStandalonePrograms } from '@vft/config/standalonePrograms.js';
import { ensureModelDepsLoaded } from '@vft/api/standaloneProgramsApi.js';
import { instantiateRemoteProgram, importRemoteModule } from '@vft/core/computed/remoteLoader.js';
import { buildProgramContext } from '@vft/core/computed/programContext.js';
import { getRuntimeConfig, effectiveBaseUrl } from '@vft/config/runtimeConfig.js';
import { runOneCalculator } from '@vft/core/computed';
import { useFileStore } from './fileStore.js';
import { useDefinitionStore } from './definitionStore.js';
import { useMappingStore } from './mappingStore.js';
import { useValueMapStore } from './valueMapStore.js';

// Estat activat/desactivat per programa de correcció (clau = id del programa del
// catàleg). NO es persisteix a localStorage: arranca buit cada sessió → tot activat
// (un programa s'omet només si l'usuari el desactiva en aquesta sessió).
function buildOutput() {
  const fileStore = useFileStore();
  const defStore = useDefinitionStore();
  const mappingStore = useMappingStore();
  const valueMapStore = useValueMapStore();
  if (!fileStore.hasData || !defStore.hasDefinition) return null;
  return buildOutputRows(
    { headers: fileStore.headers, rows: fileStore.rows },
    defStore.fields,
    mappingStore.mapping,
    {
      autoValues: mappingStore.autoValues,
      columnActions: mappingStore.columnActions,
      extraTargets: mappingStore.extraTargets,
      valueMap: valueMapStore.byField,
    },
  );
}

// Clau de rebuig: inclou el scope (pas) per evitar col·lisions quan un mateix
// camp/fila rep propostes als dos passos (p. ex. un camp email).
function rejectKey(p) {
  return `${p.scope || 'generic'}:${p.rowIndex}:${p.fieldId}`;
}

function fieldIdOf(f) {
  return f.id || f.name || f.label;
}

// Resol l'ABAST (scope) declarat d'un programa correction a un conjunt de fieldId
// (aliases) EN ABAST, donada la definició del formulari carregat:
//   ALL      → null (sense filtre: tots els camps).
//   BY_TYPE  → camps amb type ∈ field_types.
//   BY_ALIAS → els aliases triats que existeixen al formulari.
// El runtime filtra les propostes emeses a aquest conjunt (gate autoritatiu,
// independent del que faci el codi JS del programa). Sense scope_mode → ALL.
function resolveReach(program, fields) {
  const mode = (program && program.scope_mode) || 'ALL';
  if (mode === 'BY_TYPE') {
    const types = new Set(program.field_types || []);
    const ids = fields.filter((f) => types.has(f.type)).map(fieldIdOf);
    return { mode, fieldIds: new Set(ids) };
  }
  if (mode === 'BY_ALIAS') {
    const want = new Set(program.aliases || []);
    const ids = fields.filter((f) => want.has(fieldIdOf(f))).map(fieldIdOf);
    return { mode, fieldIds: new Set(ids) };
  }
  return { mode: 'ALL', fieldIds: null };
}

export const useCorrectionsStore = defineStore('corrections', {
  state: () => ({
    // Propostes del pas 1 (generals). Es calculen sobre les dades del mapeig.
    // Inclou les de les REGLES INTEGRADES (scope 'generic') i les dels PROGRAMES
    // 'correction' del catàleg (scope 'program:<id>'), fusionades.
    genericProposals: [],
    rejected: {},
    output: { headers: [], rows: [] },
    computed: false,
    running: false,
    enabledRules: {},
    // Errors d'instanciació/execució dels programes 'correction' del model (name → missatge).
    correctionProgramErrors: {},
    // Programes 'correction' carregats del catàleg en l'últim compute (per pintar-los
    // com a "regles aplicades" amb el seu toggle). [{ id, name }].
    loadedPrograms: [],
    // Estat EN VIU per programa per al timeline (ProgramPipeline): id → { status:
    // pending|running|done|error|skipped, proposals, durationMs, error, processed, total }.
    // Additiu: instrumenta el bucle de runCorrectionPrograms sense canviar-ne la lògica.
    programRuntime: {},
  }),
  getters: {
    // Aquest pas (6) només fa correccions GENERALS. Les correccions de camp
    // concret (p. ex. email) viuen al pas 7 (Càlculs), amb la resta de
    // programes per camp.
    proposals: (s) => s.genericProposals,
    acceptedProposals: (s) => s.genericProposals.filter((p) => !s.rejected[rejectKey(p)]),
    rejectedProposals: (s) => s.genericProposals.filter((p) => s.rejected[rejectKey(p)]),
    correctedRows() {
      if (!this.computed) return this.output;
      return applyCorrections(this.output, this.acceptedProposals);
    },
    correctedReport() {
      const def = useDefinitionStore();
      if (!def.hasDefinition) return { totalRows: 0, rowsWithErrors: 0, totalErrors: 0, errors: [] };
      return validateOutputRows(this.correctedRows, def.fields);
    },
    summary() {
      const accepted = this.acceptedProposals.length;
      const rejected = this.rejectedProposals.length;
      const byStrategy = {};
      for (const p of this.acceptedProposals) {
        byStrategy[p.strategy] = (byStrategy[p.strategy] || 0) + 1;
      }
      return { total: this.proposals.length, accepted, rejected, byStrategy };
    },
    // Passos del pipeline per al component ProgramPipeline (pas 6). Els programes de
    // correcció són tots de type 'correction' (sense fases preprocess/compute); l'ordre
    // és el de loadedPrograms (= ordre d'execució, per exec_order del backend).
    pipeline() {
      return this.loadedPrograms.map((p) => {
        const rt = this.programRuntime[p.id] || {};
        return {
          id: p.id,
          label: p.name,
          name: '',
          type: 'correction',
          origin: p.origin || 'catalog',
          status: rt.status || 'pending',
          proposals: rt.proposals,
          durationMs: rt.durationMs,
          error: rt.error,
          processed: rt.processed,
          total: rt.total,
        };
      });
    },
  },
  actions: {
    // Patch reactiu de l'estat en viu d'un programa per al timeline.
    setRuntime(id, patch) {
      this.programRuntime = { ...this.programRuntime, [id]: { ...(this.programRuntime[id] || {}), ...patch } };
    },
    async compute() {
      const def = useDefinitionStore();
      const out = buildOutput();
      if (!out || !def.hasDefinition) {
        this.genericProposals = [];
        this.output = { headers: [], rows: [] };
        this.computed = false;
        return;
      }
      this.running = true;
      try {
        this.output = out;
        // FONT ÚNICA: els programes 'correction' del catàleg. El component NO porta
        // cap regla de neteja (com el pas 7 amb COMPUTED_CALCULATORS=[]): tota la
        // lògica viu al catàleg i és personalitzable per client. Sense backend o
        // sense programes → cap proposta (no hi ha fallback hardcoded).
        const remote = await this.runCorrectionPrograms(out, def.fields);
        // mergeMutualSwaps: dos 'flag' mutus de relocate (A→B i B→A) es fusionen en
        // un 'swap'. És MOTOR (framework), s'aplica a totes les propostes (només toca
        // les de strategy 'relocate').
        this.genericProposals = mergeMutualSwaps(remote);
        this.computed = true;
      } finally {
        this.running = false;
      }
    },

    // Baixa els programes 'correction' del catàleg, els instancia (create(ctx)) i els
    // executa amb el motor de core/computed (mateix contracte que el pas 7). Torna
    // les propostes NORMALITZADES a la forma de correcció (scope 'program:<id>',
    // strategy = nom del programa) perquè la UI del pas 6 les tracti com una regla més.
    async runCorrectionPrograms(out, fields) {
      const errors = {};
      let programs = [];
      try {
        programs = await fetchCorrectionPrograms();
      } catch (e) {
        // Backend no disponible (standalone sense sessió…) → només programes de component.
        programs = [];
      }
      // Si hi ha programes de fitxer, assegura que les dependències del MODEL estan
      // carregades (perquè un programa en local pugui referenciar-ne) i reconstrueix
      // les entrades resolent-les. Silenciós/idempotent.
      if (getCorrectionStandalonePrograms().length) await ensureModelDepsLoaded();
      // Programes de correcció de FITXER (standalonePrograms/correction) — només
      // standalone (buit en embed). S'afegeixen als del catàleg i funcionen també
      // sense backend. Marcats amb origin:'component' per pintar-los diferent.
      programs = [...programs, ...getCorrectionStandalonePrograms()];
      // Resol l'abast de cada programa contra la definició del formulari (per filtrar
      // propostes i per pintar "Afecta:" a la UI). Clau = id del programa.
      const reaches = {};
      for (const p of programs) reaches[p.id] = resolveReach(p, fields);
      // Registra els programes carregats (per a la UI: regles aplicades + abast + toggles).
      this.loadedPrograms = programs.map((p) => {
        const r = reaches[p.id];
        return {
          id: p.id,
          name: p.description || p.name || p.id,
          origin: p.origin || 'catalog',
          affects: { mode: r.mode, aliases: r.fieldIds ? [...r.fieldIds] : null },
        };
      });
      // Inicialitza l'estat del timeline: tots a 'pending' abans d'executar.
      this.programRuntime = {};
      for (const p of programs) this.setRuntime(p.id, { status: 'pending' });
      if (!programs.length) {
        this.correctionProgramErrors = {};
        return [];
      }
      const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());
      // Dependències (datasets JSON / mòduls JS) declarades per cada programa.
      const deps = {};
      for (const p of programs) {
        for (const d of (p.dependencies || [])) {
          if (!d || !d.name || d.name in deps) continue;
          try {
            if (d.type === 'js') deps[d.name] = await importRemoteModule(d.content);
            else if (d.type === 'json' && typeof d.content === 'string') deps[d.name] = JSON.parse(d.content);
            else deps[d.name] = d.content;
          } catch {
            deps[d.name] = d.content; // no carregable → cru; el programa decideix
          }
        }
      }
      const rc = getRuntimeConfig();
      const ctx = buildProgramContext({ fields, config: {}, deps, sherpa: { baseUrl: effectiveBaseUrl(rc), token: rc.token } });
      const proposals = [];
      for (const p of programs) {
        const key = p.name || p.id;
        // Desactivat per l'usuari (toggle de "regles aplicades", clau = id del programa).
        if (this.enabledRules[p.id] === false) { this.setRuntime(p.id, { status: 'skipped' }); continue; }
        this.setRuntime(p.id, { status: 'running', processed: 0, total: out.rows.length });
        const t0 = now();
        let calc;
        try {
          calc = await instantiateRemoteProgram(p, ctx);
        } catch (err) {
          errors[key] = (err && err.stack) ? String(err.stack) : (err && err.message ? `${err.name || 'Error'}: ${err.message}` : String(err));
          this.setRuntime(p.id, { status: 'error', error: errors[key] });
          continue;
        }
        // appliesTo opcional: un programa pot declarar que no aplica al formulari actual.
        if (typeof calc.appliesTo === 'function' && !calc.appliesTo(fields, {})) { this.setRuntime(p.id, { status: 'skipped' }); continue; }
        let res;
        try {
          res = await runOneCalculator(calc, out, fields, {
            config: {},
            onEvent: (evt) => { if (evt && evt.type === 'batch') this.setRuntime(p.id, { processed: evt.processed, total: evt.total }); },
          });
        } catch (err) {
          errors[key] = (err && err.stack) ? String(err.stack) : (err && err.message ? `${err.name || 'Error'}: ${err.message}` : String(err));
          this.setRuntime(p.id, { status: 'error', error: errors[key] });
          continue;
        }
        const label = p.description || p.name || p.id;
        const scope = `program:${p.id || key}`;
        const reach = reaches[p.id];
        let kept = 0;
        for (const pr of (res.proposals || [])) {
          if (pr.fieldIndex == null || pr.fieldIndex < 0) continue;
          // GATE D'ABAST: descarta propostes fora de l'abast declarat (scope_mode),
          // sigui quin sigui el camp que el codi JS hagi tocat (per relocate: origen).
          if (reach.fieldIds && !reach.fieldIds.has(pr.fieldId)) continue;
          kept += 1;
          const orig = pr.originalValue != null
            ? pr.originalValue
            : (out.rows[pr.rowIndex] ? out.rows[pr.rowIndex][pr.fieldIndex] : null);
          // Es preserva TOT el que emet el programa (kind/target* per a relocate, que
          // porta el seu propi strategy:'relocate' = tractament natiu de la UI/motor),
          // afegint scope per fila/camp i, si el programa no fixa strategy, el seu nom.
          proposals.push({
            ...pr,
            rowIndex: pr.rowIndex,
            fieldId: pr.fieldId,
            fieldIndex: pr.fieldIndex,
            originalValue: orig,
            proposedValue: pr.proposedValue != null ? pr.proposedValue : null,
            strategy: pr.strategy || label,
            scope,
            kind: pr.kind || null,
          });
        }
        this.setRuntime(p.id, { status: 'done', proposals: kept, durationMs: now() - t0 });
      }
      this.correctionProgramErrors = errors;
      return proposals;
    },
    setRuleEnabled(strategyId, enabled) {
      this.enabledRules = { ...this.enabledRules, [strategyId]: enabled };
      if (this.computed) this.compute();
    },
    resetRules() {
      this.enabledRules = {}; // buit = tots els programes activats
      if (this.computed) this.compute();
    },
    toggleProposal(scope, rowIndex, fieldId) {
      const key = `${scope || 'generic'}:${rowIndex}:${fieldId}`;
      this.rejected = { ...this.rejected, [key]: !this.rejected[key] };
    },
    acceptAll(filter) {
      const next = { ...this.rejected };
      for (const p of this.proposals) {
        if (filter && !filter(p)) continue;
        delete next[rejectKey(p)];
      }
      this.rejected = next;
    },
    rejectAll(filter) {
      const next = { ...this.rejected };
      for (const p of this.proposals) {
        if (filter && !filter(p)) continue;
        next[rejectKey(p)] = true;
      }
      this.rejected = next;
    },
    reset() {
      this.genericProposals = [];
      this.rejected = {};
      this.output = { headers: [], rows: [] };
      this.computed = false;
    },
  },
});
